<template>
  <!-- 有角色列表：底部 van-picker 选择 -->
  <TempSelect
    v-if="!loading && useSelect"
    :key="selectKey"
    :formData="formData"
    :id="id"
    :customKey="roleCustomKey"
    :labelInfo="labelInfo"
    :required="labelInfo.required"
    :label="labelInfo.label"
    :placeholder="languages.smartOrderPleaseChoose + labelInfo.label"
    :self-service-select="true"
  />
  <!-- 无列表 / 服非 SID：手输；URL roleName 由 TempInput 预填 -->
  <TempInput
    v-else-if="!loading"
    :key="inputKey"
    :formData="formData"
    :id="id"
    :type="fieldType.roleName"
    :labelInfo="labelInfo"
    :required="labelInfo.required"
    :label="labelInfo.label"
    :placeholder="languages.smartOrderPleaseInpt + labelInfo.label"
  />
</template>

<script>
import TempSelect from './TempSelect.vue';
import TempInput from './TempInput.vue';
import { mapActions } from 'vuex';
import workTemp from '@mixins/officialSupport/workTemp';
import globalConfig from '@utiles/globalConfig';
import { fieldType } from '@configs/officialSupport/workTemp';

export default {
  name: 'TempRole',
  mixins: [workTemp],
  components: {
    TempSelect,
    TempInput
  },
  props: {
    gameId: {
      type: [String, Number],
      default: ''
    },
    /** SelfServiceForm 解析后的字段列表，用于定位 type=server */
    formFields: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      fieldType,
      roleCustomKey: {
        defaultValue: undefined,
        options: []
      },
      loading: true,
      useSelect: false,
      /** 当前游戏可选服 SID 集合；空集合表示服字段只能手输 */
      serverSidSet: null,
      loadSeq: 0
    };
  },
  computed: {
    serverFieldId() {
      const hit = (this.formFields || []).find(
        item => item && item.type === fieldType.server
      );
      return hit ? String(hit.id) : '';
    },
    /** 优先同表单 server 字段；无该字段时回退 URL serverId */
    serverValue() {
      if (this.serverFieldId) {
        return this.normalizeServerValue(this.formData?.[this.serverFieldId]);
      }
      return String(globalConfig.getHrefParam('serverId') || '').trim();
    },
    selectKey() {
      return `role-select-${this.serverValue}`;
    },
    inputKey() {
      return `role-input-${this.serverValue || 'none'}`;
    }
  },
  watch: {
    serverValue(next, prev) {
      if (next === prev) {
        return;
      }
      this.clearRoleValue();
      this.syncFromServer();
    },
    gameId(next, prev) {
      if (String(next || '') === String(prev || '')) {
        return;
      }
      this.bootstrap();
    }
  },
  created() {
    this.bootstrap();
  },
  methods: {
    ...mapActions(['getServerList', 'getCharName']),
    normalizeServerValue(val) {
      if (val == null || val === '') {
        return '';
      }
      if (typeof val === 'object') {
        return '';
      }
      return String(val).trim();
    },
    clearRoleValue() {
      if (this.formData && this.id) {
        this.$set(this.formData, this.id, '');
      }
      this.roleCustomKey = {
        defaultValue: undefined,
        options: []
      };
      this.useSelect = false;
    },
    async bootstrap() {
      this.clearRoleValue();
      this.loading = true;
      await this.loadServerSidSet();
      await this.syncFromServer();
    },
    async loadServerSidSet() {
      const gameId = String(this.gameId || '').trim();
      if (!gameId || !this.serverFieldId) {
        // 无服字段时不依赖服列表校验，URL serverId 直接视为 SID
        this.serverSidSet = null;
        return;
      }
      try {
        const data = await this.getServerList(gameId);
        const set = new Set();
        if (Array.isArray(data)) {
          data.forEach(item => {
            if (item?.SID != null && item.SID !== '') {
              set.add(String(item.SID));
            }
          });
        }
        this.serverSidSet = set;
      } catch (e) {
        this.serverSidSet = new Set();
      }
    },
    /**
     * 仅下拉 SID（或无服字段时的 URL serverId）才拉角色；
     * 服手输（服列表为空）不请求。
     */
    canFetchRoles(serverId) {
      if (!serverId) {
        return false;
      }
      if (!this.serverFieldId) {
        return true;
      }
      if (!this.serverSidSet || this.serverSidSet.size === 0) {
        return false;
      }
      return this.serverSidSet.has(String(serverId));
    },
    async syncFromServer() {
      const seq = ++this.loadSeq;
      const serverId = this.serverValue;
      const gameId = String(this.gameId || '').trim();

      if (!this.canFetchRoles(serverId) || !gameId) {
        this.useSelect = false;
        this.roleCustomKey = { defaultValue: undefined, options: [] };
        this.loading = false;
        return;
      }

      this.loading = true;
      try {
        const data = await this.getCharName({ gameId, serverId });
        if (seq !== this.loadSeq) {
          return;
        }
        if (Array.isArray(data) && data.length > 0) {
          const options = data
            .map(item => {
              const name = item?.roleName;
              if (name == null || name === '') {
                return null;
              }
              return { label: String(name), value: String(name) };
            })
            .filter(Boolean);
          if (!options.length) {
            this.useSelect = false;
            this.roleCustomKey = { defaultValue: undefined, options: [] };
            return;
          }
          let defaultValue;
          if (options.length === 1) {
            defaultValue = options[0].value;
          } else {
            const urlRole = String(
              globalConfig.getHrefParam('roleName') || ''
            ).trim();
            if (urlRole && options.some(item => item.value === urlRole)) {
              defaultValue = urlRole;
            }
          }
          this.roleCustomKey = { defaultValue, options };
          this.useSelect = true;
        } else {
          this.useSelect = false;
          this.roleCustomKey = { defaultValue: undefined, options: [] };
        }
      } catch (e) {
        if (seq !== this.loadSeq) {
          return;
        }
        this.useSelect = false;
        this.roleCustomKey = { defaultValue: undefined, options: [] };
      } finally {
        if (seq === this.loadSeq) {
          this.loading = false;
        }
      }
    }
  }
};
</script>
